import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";

export interface LoadingRowsProps {
  /** Number of `<td>` cells per row — must match the table's column count. */
  columns: number;
  /** Skeleton rows to render. Defaults to 5. */
  rows?: number;
  /**
   * Optional per-column className (indexed to match the table's columns), e.g.
   * responsive `hidden lg:table-cell`, so skeleton cells hide in sync with
   * responsively-hidden columns and the table doesn't jump width on load.
   */
  columnClassNames?: string[];
  /** Accessible loading announcement for the specific dataset. */
  label?: string;
}

/**
 * Skeleton placeholder rows for a loading table body. Renders `rows` ×
 * `columns` shimmer cells so layout doesn't jump when real data arrives.
 */
export function LoadingRows({
  columns,
  rows = 5,
  columnClassNames,
  label = "Loading results...",
}: LoadingRowsProps) {
  // Stable string keys (not the map index) keep React happy without tripping
  // noArrayIndexKey — these rows are static placeholders that never reorder.
  const rowKeys = Array.from({ length: rows }, (_, r) => `skeleton-row-${r}`);
  const cellKeys = Array.from(
    { length: columns },
    (_, c) => `skeleton-cell-${c}`
  );
  return (
    <>
      <TableRow>
        <TableCell colSpan={columns} className="h-0 p-0">
          <span role="status" className="sr-only">
            {label}
          </span>
        </TableCell>
      </TableRow>
      {rowKeys.map((rowKey) => (
        <TableRow key={rowKey} aria-hidden="true">
          {cellKeys.map((cellKey, index) => (
            <TableCell
              key={`${rowKey}-${cellKey}`}
              className={columnClassNames?.[index]}
            >
              <div className="h-4 w-full animate-pulse rounded bg-muted" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

export interface EmptyRowProps {
  /** Column count to span. */
  columns: number;
  /** Message or custom content. Defaults to "No results.". */
  children?: ReactNode;
  className?: string;
}

/** Single full-width row shown when a list returns zero items. */
export function EmptyRow({ columns, children, className }: EmptyRowProps) {
  return (
    <TableRow>
      <TableCell
        colSpan={columns}
        className={cn("py-8 text-center text-muted-foreground", className)}
      >
        {children ?? "No results."}
      </TableCell>
    </TableRow>
  );
}

export interface ErrorRowProps {
  /** Column count to span. */
  columns: number;
  /** Error message. Defaults to "Something went wrong.". */
  message?: ReactNode;
  /** When provided, renders a Retry button that calls it. */
  onRetry?: () => void;
  className?: string;
}

/** Single full-width row shown on a fetch error, with an optional Retry. */
export function ErrorRow({
  columns,
  message = "Something went wrong.",
  onRetry,
  className,
}: ErrorRowProps) {
  return (
    <TableRow>
      <TableCell
        colSpan={columns}
        className={cn("py-8 text-center", className)}
      >
        <div role="alert" className="flex flex-col items-center gap-3">
          <span className="text-sm text-destructive">{message}</span>
          {onRetry && (
            <Button
              type="button"
              variant="outline"
              size="touch"
              onClick={onRetry}
            >
              Retry
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
